import { Link } from "@/lib/router-compat";
import { Button } from "@/components/ui/button";
import { ChevronRight, Play, Lock } from "lucide-react";
import { motion } from "framer-motion";
import sentinelforgeLogo from "@/assets/sentinelforge-logo.webp";
import proposalBuilderImage from "@/assets/proposal-builder.webp";

const HeroSection = () => {
  return (
    <section className="relative pt-32 pb-20 md:pt-44 md:pb-32 overflow-hidden">
      <div className="absolute inset-0 gradient-dark" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-primary/5 blur-3xl" />
      <div className="absolute top-20 right-20 w-72 h-72 rounded-full bg-primary/3 blur-2xl" />

      <div className="relative max-w-5xl mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-primary/30 bg-primary/10 text-primary text-sm font-medium mb-8">
            <img src={sentinelforgeLogo} alt="Sentinel Forge" className="h-4 w-4 object-contain" />
            Trusted by service companies nationwide
          </div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-foreground leading-[1.1] mb-6">
            Create professional proposals and contracts{" "}
            <span className="text-gradient">in minutes</span>
          </h1>

          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8 leading-relaxed">
            For security, PI, cleaning, construction, and service companies.
            Stop spending hours on proposals — let AI handle the heavy lifting.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
            <Link to="/auth?signup">
              <Button size="lg" className="gradient-primary text-primary-foreground shadow-glow hover:opacity-90 px-8 text-base h-12">
                Start Free Trial
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/demo">
              <Button size="lg" variant="outline" className="border-border text-foreground hover:bg-secondary px-8 text-base h-12">
                <Play className="mr-2 h-4 w-4" />
                Try the Demo
              </Button>
            </Link>
          </div>

          <p className="text-sm text-muted-foreground mb-8">
            No credit card required · Instant access · 7-day free trial
          </p>

          {/* Real product screenshot, linked to the working demo. */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="max-w-3xl mx-auto"
          >
            <Link to="/demo" aria-label="Open the proposal builder demo" className="block rounded-lg border border-border shadow-elevated overflow-hidden focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring hover:border-primary/30 transition-colors">
              <img src={proposalBuilderImage} alt="Sentinel Forge Systems demo proposal form with property details, service type, staffing, and a Generate Demo Proposal button" width={848} height={518} className="w-full h-auto" fetchPriority="high" />
            </Link>
          </motion.div>

          {/* Trust badge */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="mt-8"
          >
            <Link
              to="/data-safety"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-success/30 bg-success/5 text-sm text-muted-foreground hover:border-success/50 hover:bg-success/10 transition-all"
            >
              <Lock className="h-3.5 w-3.5 text-success" />
              <span>Your data is always private and encrypted.</span>
              <span className="text-success font-medium">Learn more →</span>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
